import React, { useEffect, useState } from 'react';
import { SyncOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import bridge from './bridge';
import { useNetwork } from './NetworkGate';
import './SyncProgressBar.scss';

// Single non-blocking card showing ALL offline sync activity with a per-category breakdown:
//  - tickets/forms from the RN sync engine's outbox (`sync.progress` broadcast + `sync.status` initial)
//  - offline drafts from the FE auto-sync sweep (redux: offline.draftSync)
// Both sources are native-only, so in a plain browser `sync.status` rejects, no `sync.progress` events
// arrive, and the draft sweep never runs → no active rows → this renders null (RN-only by construction).
// Rows for a category appear only while that category is actively syncing, so the card auto-hides when
// everything finishes (the one-time completion toasts handle the "done" moment).
export default function SyncProgressBar() {
  const { online } = useNetwork();
  const [progress, setProgress] = useState(null); // tickets/forms outbox progress (from RN)
  const draftSync = useSelector((s) => s?.offline?.draftSync);

  useEffect(() => {
    let active = true;
    // Initial paint (e.g. app opened while offline with already-queued items). Rejects in the browser.
    bridge
      .call('sync.status')
      .then((s) => { if (active && s && s.progress) setProgress(s.progress); })
      .catch(() => {});
    // Live updates as items queue (offline) and upload (on reconnect).
    const off = bridge.on('sync.progress', (p) => { if (active) setProgress(p); });
    return () => { active = false; off(); };
  }, []);

  const outboxActive = !!(progress && progress.pending > 0);
  const draftActive = !!(draftSync && draftSync.active && draftSync.total > 0);

  const rows = [];
  if (outboxActive) {
    const { tickets, forms, comments } = progress;
    if (tickets && tickets.total > 0) rows.push({ key: 'tickets', label: 'Tickets', done: tickets.done, total: tickets.total });
    if (forms && forms.total > 0) rows.push({ key: 'forms', label: 'Forms', done: forms.done, total: forms.total });
    if (comments && comments.total > 0) rows.push({ key: 'comments', label: 'Comments', done: comments.done, total: comments.total });
  }
  if (draftActive) {
    rows.push({ key: 'drafts', label: 'Drafts', done: draftSync.done, total: draftSync.total });
  }

  // Show the card only while ACTIVELY syncing (online). When offline with pending work we render nothing
  // (no "Waiting to sync…" state) — it appears once the device is back online and sync is in progress.
  if (!online || !rows.length) return null;

  return (
    <div className="unified-sync" role="status" aria-live="polite">
      <div className="unified-sync__header">
        <SyncOutlined spin className="unified-sync__icon" />
        <span>Synchronizing Data…</span>
      </div>

      {rows.map((r) => {
        const percent = r.total ? Math.round((r.done / r.total) * 100) : 0;
        return (
          <div className="unified-sync__row" key={r.key}>
            <div className="unified-sync__row-head">
              <span className="unified-sync__label">{r.label}</span>
              <span className="unified-sync__count">{`${r.done}/${r.total}`}</span>
            </div>
            <div className="unified-sync__track">
              <div className="unified-sync__fill" style={{ width: `${percent}%` }} />
            </div>
            <div className="unified-sync__percent">{`${percent}%`}</div>
          </div>
        );
      })}
    </div>
  );
}
